import React, { ReactNode, useMemo } from 'react'
import {
  inspectorHoveredElementContext,
  inspectorContext,
  useInspectorOverlayEvents,
  InspectorContext,
  useHasAnyLayerWithExportableAssets,
} from '../../hooks/inspector'
import { useGetActiveSidebarTab } from '../../../components/ShareSidebarTabContext'
import { useSketchSceneTree } from '../../hooks/inspector/useSketchSceneTree'
import { useCanvasInteractions } from '../../../../canvas/components/CanvasInteractions'

type FrameInspectorProviderProps = {
  children: ReactNode
  /**
   * Automatically select the top-level frame element when the inspector is active.
   * This can only work if the root is a top-level frame element (frame view).
   */
  autoSelect: boolean
}

export function FrameInspectorProvider({
  children,
  autoSelect,
}: FrameInspectorProviderProps) {
  const activeSidebarTab = useGetActiveSidebarTab()
  const isInspectorActive = activeSidebarTab === 'Inspect'

  const { sketchRootElement, hasFailed, isTreeFullyLoaded } =
    useSketchSceneTree(isInspectorActive)

  const hasAnyLayerWithExportableAssets =
    useHasAnyLayerWithExportableAssets(sketchRootElement)

  const { isEnabled: isCanvasInteractionsEnabled } = useCanvasInteractions()

  // Enable inspector interactions only when inspector is active and canvas interactions are enabled
  const enableInspectorInteractions =
    isInspectorActive && isCanvasInteractionsEnabled

  const {
    selectedElement,
    hoveredElement,
    setSelectedElement,
    setHoveredElement,
    clickHandler,
    mouseMoveHandler,
    mouseDownHandle,
  } = useInspectorOverlayEvents(
    sketchRootElement,
    enableInspectorInteractions,
    autoSelect,
    isTreeFullyLoaded
  )

  let inspectorStatus: InspectorContext['inspectorStatus'] = 'pending'

  if (sketchRootElement) {
    inspectorStatus = 'ready' as const
  } else if (hasFailed) {
    inspectorStatus = 'errored' as const
  }

  const inspectorContextValue = useMemo((): InspectorContext => {
    return {
      selectedElement: selectedElement,
      isInspectorActive: isInspectorActive,
      sketchSceneRootElement: sketchRootElement,
      setSelectedElement: setSelectedElement,
      setHoveredElement,
      inspectorStatus,
      hasAnyLayerWithExportableAssets,
      clickHandler,
      mouseMoveHandler,
      mouseDownHandle,
      isTreeFullyLoaded,
    }
  }, [
    selectedElement,
    isInspectorActive,
    sketchRootElement,
    setSelectedElement,
    setHoveredElement,
    inspectorStatus,
    hasAnyLayerWithExportableAssets,
    clickHandler,
    mouseMoveHandler,
    mouseDownHandle,
    isTreeFullyLoaded,
  ])

  return (
    <inspectorContext.Provider value={inspectorContextValue}>
      <inspectorHoveredElementContext.Provider value={hoveredElement}>
        {children}
      </inspectorHoveredElementContext.Provider>
    </inspectorContext.Provider>
  )
}
